import { throttle } from "./throttle";
import { nextFrame } from "./next-frame";

// @TODO check visibility window.onLoad for edge cases?

/*
 * Registered DOM nodes where the keys are the actual nodes and the values
 * the callback functions.
 */
const registry = new Map();

/*
 * Set true when visibility checks should be temporary
 * disabled, e.g. during automatic scrolling.
 */
let paused = false;

let innerHeight = null;
const getInnerHeight = () => {
	if (innerHeight === null) {
		innerHeight = window.innerHeight;
		window.addEventListener(
			"resize",
			throttle(250, () => {
				innerHeight = window.innerHeight;
			}),
			{ passive: true },
		);
	}
	return innerHeight;
};

/*
 * Returns true if a DOM node is currently visible.
 */
const isVisible = (domNode, padding = 0) => {
	if (paused) {
		return false;
	}
	const { top, height, left } = domNode.getBoundingClientRect();
	return (
		(height != 0 || top != 0 || left != 0) &&
		top + height + padding >= 0 &&
		getInnerHeight() - top + padding > 0
	);
};

const isVisibleForSomeTime = (domNode, padding = 0, time = 0) => {
	if (!isVisible(domNode, padding)) {
		return Promise.resolve(false);
	}
	if (time == 0) {
		return Promise.resolve(true);
	}
	return new Promise(resolver => {
		setTimeout(() => {
			resolver(isVisible(domNode, padding));
		}, time);
	});
};

/*
 * Checks visibility of all nodes, call handler and unregister node if it's
 * visible.
 */
export const checkVisibility = throttle(500, () => {
	registry.forEach(async ({ resolver, padding, time }, domNode) => {
		const seen = await isVisibleForSomeTime(domNode, padding, time);
		if (seen) {
			registry.delete(domNode);
			resolver();
		}
	});
});

/*
 * Check visibility on document scroll and window resize.
 */
document.addEventListener("scroll", checkVisibility, { passive: true });
window.addEventListener("resize", checkVisibility, { passive: true });

/*
 * The only exported function: register a node and get a promise back.
 */
export const visible = (domNode, padding = 0, time = 0) =>
	new Promise(resolver => {
		// Checking visibility is very expensive, so better wait a bit not to block
		// UI for too long.
		nextFrame().then(() => {
			isVisibleForSomeTime(domNode, padding, time).then(seen => {
				if (seen) {
					resolver();
				} else {
					registry.set(domNode, { resolver, padding, time });
				}
			});
		});
	});

export const stopCheck = domNode => {
	registry.delete(domNode);
};

export function pause() {
	paused = true;
}

export function resume() {
	paused = false;
	checkVisibility();
}
